import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from './Card'
import Button from './Button'
import { Sparkles, Lightbulb, ArrowRight, X, AlertTriangle } from 'lucide-react'

const CarePlanSuggestions = ({ clientId, onApplySuggestion }) => {
  const { t } = useTranslation()
  const [suggestions, setSuggestions] = useState([])
  const [abnormalVitals, setAbnormalVitals] = useState([])
  const [loading, setLoading] = useState(true)
  const organizationId = useOrganizationId()

  // Normal ranges for vital signs
  const VITAL_RANGES = {
    bloodPressure: { normal: { sys: [90, 120], dia: [60, 80] }, elevated: { sys: [120, 129], dia: [60, 80] }, high1: { sys: [130, 139], dia: [80, 89] }, high2: { sys: [140, Infinity], dia: [90, Infinity] } },
    heartRate: { normal: [60, 100] },
    respiratoryRate: { normal: [12, 20] },
    temperature: { normal: [36.1, 37.2] },
    oxygenSaturation: { normal: [95, 100] },
    bmi: { normal: [18.5, 24.9], overweight: [25, 29.9], obese: [30, Infinity] }
  }

  // Analyze vital signs and identify abnormalities
  const analyzeVitalSigns = (vitalSigns) => {
    if (!vitalSigns) return []
    
    const abnormalities = []

    // Blood Pressure
    if (vitalSigns.bloodPressure) {
      const bpMatch = vitalSigns.bloodPressure.toString().match(/(\d+)\/(\d+)/)
      if (bpMatch) {
        const systolic = parseInt(bpMatch[1])
        const diastolic = parseInt(bpMatch[2])
        
        if (systolic >= 140 || diastolic >= 90) {
          abnormalities.push({
            name: t('carePlans.suggestions.vitals.bloodPressure'),
            value: vitalSigns.bloodPressure,
            status: 'critical',
            message: t('carePlans.suggestions.vitals.bpCritical', { value: vitalSigns.bloodPressure })
          })
        } else if (systolic >= 130 || diastolic >= 80) {
          abnormalities.push({
            name: t('carePlans.suggestions.vitals.bloodPressure'),
            value: vitalSigns.bloodPressure,
            status: 'warning',
            message: t('carePlans.suggestions.vitals.bpWarning', { value: vitalSigns.bloodPressure })
          })
        } else if (systolic >= 120) {
          abnormalities.push({
            name: t('carePlans.suggestions.vitals.bloodPressure'),
            value: vitalSigns.bloodPressure,
            status: 'caution',
            message: t('carePlans.suggestions.vitals.bpCaution', { value: vitalSigns.bloodPressure })
          })
        }
      }
    }

    // Heart Rate
    if (vitalSigns.heartRate) {
      const hr = parseFloat(vitalSigns.heartRate)
      if (hr < 60) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.heartRate'),
          value: `${hr} ${t('carePlans.suggestions.vitals.bpm')}`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.hrLow', { value: hr })
        })
      } else if (hr > 100) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.heartRate'),
          value: `${hr} ${t('carePlans.suggestions.vitals.bpm')}`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.hrHigh', { value: hr })
        })
      }
    }

    // Respiratory Rate
    if (vitalSigns.respiratoryRate) {
      const rr = parseFloat(vitalSigns.respiratoryRate)
      if (rr < 12) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.respiratoryRate'),
          value: `${rr} ${t('carePlans.suggestions.vitals.breathsMin')}`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.rrLow', { value: rr })
        })
      } else if (rr > 20) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.respiratoryRate'),
          value: `${rr} ${t('carePlans.suggestions.vitals.breathsMin')}`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.rrHigh', { value: rr })
        })
      }
    }

    // Temperature
    if (vitalSigns.temperature) {
      const temp = parseFloat(vitalSigns.temperature)
      if (temp < 36.1) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.temperature'),
          value: `${temp}°C`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.tempLow', { value: temp })
        })
      } else if (temp > 37.2) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.temperature'),
          value: `${temp}°C`,
          status: 'warning',
          message: t('carePlans.suggestions.vitals.tempHigh', { value: temp })
        })
      }
    }

    // Oxygen Saturation
    if (vitalSigns.oxygenSaturation) {
      const spo2 = parseFloat(vitalSigns.oxygenSaturation)
      if (spo2 < 95) {
        abnormalities.push({
          name: t('carePlans.suggestions.vitals.oxygenSaturation'),
          value: `${spo2}%`,
          status: 'critical',
          message: t('carePlans.suggestions.vitals.spo2Low', { value: spo2 })
        })
      }
    }

    // BMI
    if (vitalSigns.bmi) {
      const bmi = parseFloat(vitalSigns.bmi)
      if (bmi < 18.5) {
        abnormalities.push({
          name: 'BMI',
          value: bmi.toFixed(1),
          status: 'warning',
          message: t('carePlans.suggestions.vitals.bmiLow', { value: bmi.toFixed(1) })
        })
      } else if (bmi >= 30) {
        abnormalities.push({
          name: 'BMI',
          value: bmi.toFixed(1),
          status: 'warning',
          message: t('carePlans.suggestions.vitals.bmiHigh', { value: bmi.toFixed(1) })
        })
      } else if (bmi >= 25) {
        abnormalities.push({
          name: 'BMI',
          value: bmi.toFixed(1),
          status: 'caution',
          message: t('carePlans.suggestions.vitals.bmiOverweight', { value: bmi.toFixed(1) })
        })
      }
    }

    return abnormalities
  }

  useEffect(() => {
    if (clientId && organizationId) {
      generateSuggestions()
    }
  }, [clientId, organizationId])

  const generateSuggestions = async () => {
    if (!clientId || !organizationId) return

    try {
      setLoading(true)

      // Get client's medical history
      const { data: history } = await supabase
        .from('medical_histories')
        .select('*')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      // Analyze vital signs for abnormalities
      const vitalAbnormalities = analyzeVitalSigns(history?.vital_signs)
      setAbnormalVitals(vitalAbnormalities)

      // Get client's previous care plans
      const { data: previousPlans } = await supabase
        .from('care_plans')
        .select('*')
        .eq('client_id', clientId)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(5)

      // Generate suggestions based on data
      const generatedSuggestions = []

      // Priority Suggestion: Based on abnormal vital signs - Root Cause Analysis
      if (vitalAbnormalities.length > 0) {
        const criticalAbnormalities = vitalAbnormalities.filter(v => v.status === 'critical')
        const warningAbnormalities = vitalAbnormalities.filter(v => v.status === 'warning')
        
        if (criticalAbnormalities.length > 0) {
          generatedSuggestions.push({
            id: 'suggestion-vitals-critical',
            title: t('carePlans.suggestions.vitalsCritical.title'),
            category: 'assessment',
            description: t('carePlans.suggestions.vitalsCritical.description', { vitals: criticalAbnormalities.map(v => v.name).join(', ') }),
            rationale: t('carePlans.suggestions.vitalsCritical.rationale'),
            priority: 'high',
            rootCauseFocus: true,
            abnormalVitals: criticalAbnormalities
          })
        } else if (warningAbnormalities.length > 0) {
          generatedSuggestions.push({
            id: 'suggestion-vitals-warning',
            title: t('carePlans.suggestions.vitalsWarning.title'),
            category: 'assessment',
            description: t('carePlans.suggestions.vitalsWarning.description', { vitals: warningAbnormalities.map(v => v.name).join(', ') }),
            rationale: t('carePlans.suggestions.vitalsWarning.rationale'),
            priority: 'high',
            rootCauseFocus: true,
            abnormalVitals: warningAbnormalities
          })
        } else {
          const cautionAbnormalities = vitalAbnormalities.filter(v => v.status === 'caution')
          if (cautionAbnormalities.length > 0) {
            generatedSuggestions.push({
              id: 'suggestion-vitals-caution',
              title: t('carePlans.suggestions.vitalsCaution.title'),
              category: 'assessment',
              description: t('carePlans.suggestions.vitalsCaution.description', { vitals: cautionAbnormalities.map(v => v.name).join(', ') }),
              rationale: t('carePlans.suggestions.vitalsCaution.rationale'),
              priority: 'medium',
              rootCauseFocus: true,
              abnormalVitals: cautionAbnormalities
            })
          }
        }
      }

      // Suggestion 1: Based on chronic conditions - Root Cause Focus
      if (history?.chronic_conditions) {
        const conditions = history.chronic_conditions.toLowerCase()
        if (conditions.includes('diabetes') || conditions.includes('blood sugar')) {
          generatedSuggestions.push({
            id: 'suggestion-1',
            title: t('carePlans.suggestions.diabetes.title'),
            category: 'nutrition',
            description: t('carePlans.suggestions.diabetes.description'),
            rationale: t('carePlans.suggestions.diabetes.rationale'),
            priority: 'high',
            rootCauseFocus: true,
          })
        }
        if (conditions.includes('inflammation') || conditions.includes('arthritis')) {
          generatedSuggestions.push({
            id: 'suggestion-2',
            title: t('carePlans.suggestions.inflammation.title'),
            category: 'nutrition',
            description: t('carePlans.suggestions.inflammation.description'),
            rationale: t('carePlans.suggestions.inflammation.rationale'),
            priority: 'high',
            rootCauseFocus: true,
          })
        }
      }

      // Suggestion 2: Based on stress level - Root Cause Focus
      if (history?.stress_level) {
        const stressLevel = history.stress_level.toLowerCase()
        if (stressLevel === 'high' || stressLevel === 'very high') {
          generatedSuggestions.push({
            id: 'suggestion-3',
            title: t('carePlans.suggestions.stress.title'),
            category: 'wellness',
            description: t('carePlans.suggestions.stress.description'),
            rationale: t('carePlans.suggestions.stress.rationale'),
            priority: 'high',
            rootCauseFocus: true,
          })
        }
      }

      // Suggestion 3: Based on sleep issues - Root Cause Focus
      if (history?.sleep && (history.sleep.toLowerCase().includes('poor') || history.sleep.toLowerCase().includes('insomnia'))) {
        generatedSuggestions.push({
          id: 'suggestion-4',
          title: t('carePlans.suggestions.sleep.title'),
          category: 'wellness',
          description: t('carePlans.suggestions.sleep.description'),
          rationale: t('carePlans.suggestions.sleep.rationale'),
          priority: 'high',
          rootCauseFocus: true,
        })
      }

      // Suggestion 4: Based on diet - Root Cause Focus
      if (history?.diet && history.diet.toLowerCase().includes('processed') || history.diet.toLowerCase().includes('fast food')) {
        generatedSuggestions.push({
          id: 'suggestion-5',
          title: t('carePlans.suggestions.diet.title'),
          category: 'nutrition',
          description: t('carePlans.suggestions.diet.description'),
          rationale: t('carePlans.suggestions.diet.rationale'),
          priority: 'high',
          rootCauseFocus: true,
        })
      }

      // Suggestion 5: Based on previous successful plans
      if (previousPlans && previousPlans.length > 0) {
        const completedPlans = previousPlans.filter((p) => p.status === 'completed')
        if (completedPlans.length > 0) {
          const mostRecent = completedPlans[0]
          generatedSuggestions.push({
            id: 'suggestion-6',
            title: t('carePlans.suggestions.followup.title', { title: mostRecent.title }),
            category: 'general',
            description: t('carePlans.suggestions.followup.description', { title: mostRecent.title }),
            rationale: t('carePlans.suggestions.followup.rationale'),
            priority: 'low',
          })
        }
      }

      // Suggestion 6: Based on primary goals - Root Cause Focus
      if (history?.primary_goals) {
        const goals = history.primary_goals.toLowerCase()
        if (goals.includes('weight') || goals.includes('loss')) {
          generatedSuggestions.push({
            id: 'suggestion-7',
            title: t('carePlans.suggestions.weight.title'),
            category: 'nutrition',
            description: t('carePlans.suggestions.weight.description'),
            rationale: t('carePlans.suggestions.weight.rationale'),
            priority: 'high',
            rootCauseFocus: true,
          })
        }
      }

      setSuggestions(generatedSuggestions.slice(0, 5)) // Limit to 5 suggestions
    } catch (error) {
      console.error('Error generating suggestions:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">{t('carePlans.suggestions.loading')}</div>
  }

  if (suggestions.length === 0) {
    return (
      <Card variant="soft" className="p-6 text-center">
        <Lightbulb size={32} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('carePlans.suggestions.noSuggestions')}</p>
      </Card>
    )
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'critical':
        return 'bg-red-50 border-red-300 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'
      case 'warning':
        return 'bg-orange-50 border-orange-300 text-orange-800 dark:bg-orange-900/20 dark:border-orange-700 dark:text-orange-300'
      case 'caution':
        return 'bg-yellow-50 border-yellow-300 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-300'
      default:
        return 'bg-gray-50 border-gray-300 text-gray-800'
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles size={20} />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('carePlans.suggestions.title')}</h3>
      </div>

      {/* Abnormal Vital Signs Alert */}
      {abnormalVitals.length > 0 && (
        <Card variant="soft" className="p-4 border-2 border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-semibold text-orange-900 dark:text-orange-200 mb-2">
                {t('carePlans.suggestions.abnormalVitals.title')}
              </h4>
              <div className="space-y-2">
                {abnormalVitals.map((vital, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded-lg border ${getStatusColor(vital.status)}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold">{vital.name}:</span>
                      <span className="font-mono">{vital.value}</span>
                    </div>
                    <p className="text-xs mt-1">{vital.message}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-orange-800 dark:text-orange-300 mt-3 italic">
                <strong>{t('carePlans.suggestions.rootCausePrinciple')}:</strong> {t('carePlans.suggestions.abnormalVitals.principle')}
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {suggestions.map((suggestion) => (
          <Card
            key={suggestion.id}
            variant="soft"
            className={`p-4 border-2 transition-all ${
              suggestion.rootCauseFocus
                ? 'border-primary-300 dark:border-primary-600 bg-primary-50/30 dark:bg-primary-900/10'
                : 'border-transparent hover:border-primary-200'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h4 className="font-semibold text-gray-900 dark:text-white">{suggestion.title}</h4>
                  {suggestion.rootCauseFocus && (
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-600">
                      {t('carePlans.suggestions.rootCauseFocus')}
                    </span>
                  )}
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(
                      suggestion.priority
                    )}`}
                  >
                    {t(`carePlans.suggestions.priority.${suggestion.priority}`)} {t('carePlans.suggestions.priority.label')}
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 capitalize">
                    {t(`carePlans.suggestions.category.${suggestion.category}`)}
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{suggestion.description}</p>
                {suggestion.rootCauseFocus && (
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-300">
                    <strong>{t('carePlans.suggestions.ellenWhitePrinciple')}:</strong> {t('carePlans.suggestions.ellenWhiteQuote')}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 italic mt-2">{t('carePlans.suggestions.rationale')}: {suggestion.rationale}</p>
              </div>
            </div>
            {onApplySuggestion && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onApplySuggestion(suggestion)}
                className="flex items-center gap-1"
              >
                {t('carePlans.suggestions.applySuggestion')}
                <ArrowRight size={14} />
              </Button>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}

export default CarePlanSuggestions

